import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Container,
    Typography,
    TextField,
    Grid,
    Chip,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Button,
    useTheme,
    useMediaQuery,
    alpha,
    Tooltip,
    Pagination,
    Stack,
    TableSortLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PeopleIcon from '@mui/icons-material/People';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SortIcon from '@mui/icons-material/Sort';
import EditIcon from '@mui/icons-material/Edit';
import { guardiansService } from '../../../../services/api/guardians';
import { Guardian, GuardiansQueryParams } from '../../../../types/guardian';
import { format } from 'date-fns';
import { keyframes } from '@mui/system';

const filterContainerStyles = (theme: any) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    marginBottom: 3,
    padding: 3,
    borderRadius: 2,
    background: `linear-gradient(165deg, 
        ${alpha(theme.palette.background.default, 0.95)} 0%, 
        ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
    '& .filter-header': {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        marginBottom: 1,
        '& .MuiTypography-root': {
            fontWeight: 600,
            color: theme.palette.primary.main,
        },
    },
    '& .filter-content': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    '& .filter-group': {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 200,
        flex: 1,
        '& .MuiFormControl-root': {
            width: '100%',
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary,
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            '&:hover': {
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
            },
            '&.Mui-focused': {
                backgroundColor: alpha(theme.palette.background.paper, 1),
            },
        },
    },
    '& .filter-actions': {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        marginTop: 1,
        paddingTop: 2,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    },
});

const tableStyles = {
    borderRadius: (theme: any) => theme.spacing(2),
    background: (theme: any) => `linear-gradient(165deg, 
        ${alpha(theme.palette.background.default, 0.92)} 0%, 
        ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
    backdropFilter: 'blur(10px)',
    border: (theme: any) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    boxShadow: (theme: any) => `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
    '& .MuiTableHead-root': {
        background: (theme: any) => alpha(theme.palette.primary.main, 0.05),
        '& .MuiTableCell-head': {
            fontWeight: 600,
            color: (theme: any) => theme.palette.primary.main,
            borderBottom: (theme: any) => `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        },
    },
    '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
            transition: 'background-color 0.2s ease',
            '&:hover': {
                backgroundColor: (theme: any) => alpha(theme.palette.primary.main, 0.04),
            },
        },
    },
};

const shimmerAnimation = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const floatAnimation = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const glowAnimation = keyframes`
  0% {
    box-shadow: 0 0 5px ${alpha('#000', 0.1)};
  }
  50% {
    box-shadow: 0 0 20px ${alpha('#000', 0.2)};
  }
  100% {
    box-shadow: 0 0 5px ${alpha('#000', 0.1)};
  }
`;

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    background: `linear-gradient(145deg, 
        ${alpha(theme.palette.background.paper, 0.8)} 0%, 
        ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
    borderRadius: theme.spacing(2),
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
})) as unknown as typeof Box;

const StyledOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: alpha(theme.palette.background.paper, 0.7),
    backdropFilter: 'blur(4px)',
    zIndex: 1,
    borderRadius: theme.spacing(2),
})) as unknown as typeof Box;

interface StyledCardProps {
    theme?: Theme;
}

const StyledCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'theme',
})<StyledCardProps>(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'visible',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    background: `linear-gradient(135deg, 
        ${alpha(theme?.palette.background.paper || '#fff', 0.95)} 0%, 
        ${alpha(theme?.palette.background.default || '#fff', 0.98)} 100%)`,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${alpha(theme?.palette.divider || '#000', 0.1)}`,
    borderRadius: theme?.spacing(2) || 16,
    boxShadow: `0 4px 20px ${alpha(theme?.palette.common.black || '#000', 0.08)}`,
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: `0 12px 32px ${alpha(theme?.palette.common.black || '#000', 0.12)}`,
    },
}));

interface StyledCardContentProps {
    theme?: Theme;
}

const StyledCardContent = styled(CardContent, {
    shouldForwardProp: (prop) => prop !== 'theme',
})<StyledCardContentProps>(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme?.spacing(1.5) || 12,
    padding: theme?.spacing(2) || 16,
    '&:last-child': {
        paddingBottom: theme?.spacing(2) || 16,
    },
}));

const CardHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    background: alpha(theme.palette.background.paper, 0.8),
    borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
}));

const CardAvatar = styled(Box)(({ theme }) => ({
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    transition: 'all 0.3s ease',
    '& .MuiSvgIcon-root': {
        fontSize: 28,
    },
}));

const StatusChip = styled(Chip)(({ theme }: { theme: Theme }) => ({
    height: 28,
    borderRadius: 14,
    padding: '0 12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    transition: 'all 0.2s ease',
    '&.active': {
        backgroundColor: alpha(theme.palette.success.main, 0.1),
        color: theme.palette.success.main,
        '&:hover': {
            backgroundColor: alpha(theme.palette.success.main, 0.2),
        },
    },
    '&.inactive': {
        backgroundColor: alpha(theme.palette.error.main, 0.1),
        color: theme.palette.error.main,
        '&:hover': {
            backgroundColor: alpha(theme.palette.error.main, 0.2),
        },
    },
}));

const InfoRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    background: alpha(theme.palette.background.paper, 0.5),
    '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
        fontSize: '1.25rem',
    },
    '& .label': {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    '& .value': {
        color: theme.palette.text.primary,
        fontSize: '0.875rem',
        fontWeight: 500,
    },
}));

const CardFooter = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'auto',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    background: alpha(theme.palette.background.paper, 0.8),
    borderRadius: `0 0 ${theme.spacing(2)} ${theme.spacing(2)}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }: { theme: Theme }) => ({
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.08)}`,
    background: `linear-gradient(165deg, 
        ${alpha(theme.palette.background.default, 0.95)} 0%, 
        ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    '& .MuiTable-root': {
        borderCollapse: 'separate',
        borderSpacing: '0 8px',
    },
    '& .MuiTableHead-root': {
        '& .MuiTableRow-root': {
            background: 'transparent',
        },
        '& .MuiTableCell-head': {
            borderBottom: 'none',
            padding: theme.spacing(2),
            fontWeight: 600,
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            '& .MuiTableSortLabel-root': {
                '&:hover': {
                    color: theme.palette.primary.main,
                },
                '&.MuiTableSortLabel-active': {
                    color: theme.palette.primary.main,
                    '& .MuiTableSortLabel-icon': {
                        color: theme.palette.primary.main,
                    },
                },
            },
        },
    },
    '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
            transition: 'all 0.2s ease',
            background: alpha(theme.palette.background.paper, 0.8),
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.08)}`,
                background: alpha(theme.palette.background.paper, 0.95),
            },
            '& .MuiTableCell-root': {
                borderBottom: 'none',
                padding: theme.spacing(2),
                '&:first-of-type': {
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                },
                '&:last-of-type': {
                    borderTopRightRadius: theme.shape.borderRadius,
                    borderBottomRightRadius: theme.shape.borderRadius,
                },
            },
        },
    },
}));

const GuardianAvatar = styled(Avatar)(({ theme }: { theme: Theme }) => ({
    width: 40,
    height: 40,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    transition: 'all 0.2s ease',
    '&:hover': {
        transform: 'scale(1.1)',
        borderColor: theme.palette.primary.main,
    },
}));

const StatusBadge = styled(Badge)(({ theme }: { theme: Theme }) => ({
    '& .MuiBadge-badge': {
        width: 10,
        height: 10,
        borderRadius: '50%',
        border: `2px solid ${theme.palette.background.paper}`,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.background.paper, 0.8)}`,
    },
    '& .MuiBadge-badge.active': {
        backgroundColor: theme.palette.success.main,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.success.main, 0.2)}`,
    },
    '& .MuiBadge-badge.inactive': {
        backgroundColor: theme.palette.error.main,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, 0.2)}`,
    },
}));

const ActionButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
    padding: theme.spacing(1),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        transform: 'scale(1.1)',
    },
}));

export const GuardiansScreen: React.FC = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isRTL = i18n.language === 'ar';

    // Debug logs for i18n
    console.log('Current language:', i18n.language);
    // Log the full translation object for admin.guardians
    try {
        // @ts-ignore
        const adminGuardians = i18n.getResource(i18n.language, 'translation', 'admin.guardians');
        console.log('admin.guardians resource:', adminGuardians);
    } catch (e) {
        console.warn('Could not get admin.guardians resource:', e);
    }
    console.log("t('admin.guardians.title'):", t('admin.guardians.title'));

    const [guardians, setGuardians] = useState<Guardian[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalGuardians, setTotalGuardians] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<'active' | 'inactive' | ''>('');
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [sortBy, setSortBy] = useState<keyof Guardian>('created_at');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    const fetchGuardians = async (params: GuardiansQueryParams) => {
        setLoading(true);
        setError(null);
        try {
            const response = await guardiansService.getGuardians({
                ...params,
                page: (params.page || 0) + 1, // Convert to 1-based index for API
                per_page: params.per_page,
            });
            
            setGuardians(response.data);
            setTotalGuardians(response.meta.total);
        } catch (err) {
            setError(t('admin.guardians.errors.fetchFailed'));
            console.error('Error fetching guardians:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearch(value);
        
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            setPage(0);
            fetchGuardians({
                page: 0,
                per_page: rowsPerPage,
                search: value,
                status: status as 'active' | 'inactive',
            });
        }, 500);

        setSearchTimeout(timeout);
    }, [status, rowsPerPage]);

    const handleStatusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const newStatus = event.target.value as 'active' | 'inactive' | '';
        setStatus(newStatus);
        setPage(0);
        fetchGuardians({
            page: 0,
            per_page: rowsPerPage,
            search,
            status: newStatus as 'active' | 'inactive',
        });
    };

    const handleSort = (property: keyof Guardian) => {
        const isAsc = sortBy === property && sortDirection === 'asc';
        setSortDirection(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        fetchGuardians({
            page,
            per_page: rowsPerPage,
            search,
            status: status as 'active' | 'inactive',
            sort_by: property,
            sort_direction: isAsc ? 'desc' : 'asc',
        });
    };

    useEffect(() => {
        fetchGuardians({
            page,
            per_page: rowsPerPage,
            search,
            status: status as 'active' | 'inactive',
        });
    }, [page, rowsPerPage]);

    const renderGuardianRow = (guardian: Guardian) => (
        <TableRow key={guardian.id} hover>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StatusBadge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                        className={guardian.status}
                    >
                        <GuardianAvatar>
                            <PersonIcon />
                        </GuardianAvatar>
                    </StatusBadge>
                    <Box>
                        <Typography variant="subtitle2" sx={{ 
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.5
                        }}>
                            {guardian.name}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                            color: 'text.secondary',
                            display: 'block',
                            fontSize: '0.75rem'
                        }}>
                            ID: {guardian.id}
                        </Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" sx={{ color: 'primary.main', opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {guardian.email}
                    </Typography>
                </Box>
            </TableCell>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" sx={{ color: 'primary.main', opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {guardian.phone}
                    </Typography>
                </Box>
            </TableCell>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon fontSize="small" sx={{ color: 'primary.main', opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ 
                        color: 'text.primary',
                        fontWeight: 500
                    }}>
                        {guardian.students_count}
                    </Typography>
                </Box>
            </TableCell>
            <TableCell>
                <StatusChip
                    label={t(`admin.guardians.status.${guardian.status}`)}
                    size="small"
                    className={guardian.status}
                />
            </TableCell>
            <TableCell>
                <Tooltip title={format(new Date(guardian.created_at), 'PPP')}>
                    <Typography variant="body2" sx={{ 
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <CalendarTodayIcon fontSize="small" sx={{ opacity: 0.7 }} />
                        {format(new Date(guardian.created_at), 'MMM d, yyyy')}
                    </Typography>
                </Tooltip>
            </TableCell>
            <TableCell>
                <Stack direction="row" spacing={1}>
                    <Tooltip title={t('common.viewDetails')}>
                        <ActionButton size="small" color="primary">
                            <PersonIcon fontSize="small" />
                        </ActionButton>
                    </Tooltip>
                    <Tooltip title={t('common.edit')}>
                        <ActionButton size="small" color="primary">
                            <EditIcon fontSize="small" />
                        </ActionButton>
                    </Tooltip>
                </Stack>
            </TableCell>
        </TableRow>
    );

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    {t('admin.guardians.title')}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<PersonIcon />}
                    onClick={() => {/* Handle add new guardian */}}
                >
                    {t('admin.guardians.addNew')}
                </Button>
            </Box>

            <Box sx={filterContainerStyles}>
                <Box className="filter-header">
                    <FilterListIcon color="primary" />
                    <Typography variant="h6">
                        {t('admin.guardians.filters.title')}
                    </Typography>
                </Box>

                <Box className="filter-content">
                    <Box className="filter-group">
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder={t('admin.guardians.filters.search')}
                            value={search}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: loading ? (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ) : search ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => {
                                                setSearch('');
                                                setPage(0);
                                                fetchGuardians({
                                                    page: 0,
                                                    per_page: rowsPerPage,
                                                    status: status as 'active' | 'inactive',
                                                });
                                            }}
                                            size="small"
                                            edge="end"
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </Box>

                    <Box className="filter-group">
                        <FormControl fullWidth>
                            <InputLabel>{t('admin.guardians.status.label')}</InputLabel>
                            <Select
                                value={status}
                                onChange={handleStatusChange}
                                label={t('admin.guardians.status.label')}
                                displayEmpty
                                renderValue={(value: string) => value === '' ? t('common.all') : t(`admin.guardians.status.${value}`)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <PersonIcon color="action" />
                                    </InputAdornment>
                                }
                            >
                                <MenuItem value="">{t('common.all')}</MenuItem>
                                <MenuItem value="active">{t('admin.guardians.status.active')}</MenuItem>
                                <MenuItem value="inactive">{t('admin.guardians.status.inactive')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                <Box className="filter-actions">
                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={<CloseIcon />}
                        onClick={() => {
                            setSearch('');
                            setStatus('');
                            setPage(0);
                            fetchGuardians({
                                page: 0,
                                per_page: rowsPerPage,
                            });
                        }}
                    >
                        {t('common.clearFilters')}
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && guardians.length === 0 ? (
                <StyledBox>
                    <CircularProgress size={40} />
                    <Typography variant="h6" color="textSecondary">
                        {t('admin.guardians.loading')}
                    </Typography>
                </StyledBox>
            ) : guardians.length === 0 ? (
                <StyledBox>
                    <SearchOffIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    <Typography variant="h6" color="textSecondary" align="center">
                        {t('admin.guardians.noGuardians')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" align="center">
                        {t('admin.guardians.filters.search')}
                    </Typography>
                </StyledBox>
            ) : (
                <Box sx={{ position: 'relative' }}>
                    {loading && (
                        <StyledOverlay>
                            <CircularProgress size={40} />
                        </StyledOverlay>
                    )}
                    
                    <StyledTableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {t('admin.guardians.name')}
                                    </TableCell>
                                    <TableCell>
                                        {t('admin.guardians.email')}
                                    </TableCell>
                                    <TableCell>
                                        {t('admin.guardians.phone')}
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'students_count'}
                                            direction={sortBy === 'students_count' ? sortDirection : 'asc'}
                                            onClick={() => handleSort('students_count')}
                                        >
                                            {t('admin.guardians.students_count')}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        {t('admin.guardians.status.label')}
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'created_at'}
                                            direction={sortBy === 'created_at' ? sortDirection : 'asc'}
                                            onClick={() => handleSort('created_at')}
                                        >
                                            {t('admin.guardians.created_at')}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>{t('common.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {guardians.map(renderGuardianRow)}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>

                    <Box 
                        sx={{ 
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                            background: alpha(theme.palette.background.paper, 0.8),
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(2),
                            backdropFilter: 'blur(8px)',
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            {t('common.showing')} {page * rowsPerPage + 1} - {Math.min((page + 1) * rowsPerPage, totalGuardians)} {t('common.of')} {totalGuardians}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                    value={rowsPerPage}
                                    onChange={handleChangeRowsPerPage}
                                    displayEmpty
                                >
                                    {[20, 40, 60, 80].map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {value} {t('common.rowsPerPage')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Pagination
                                count={Math.ceil(totalGuardians / rowsPerPage)}
                                page={page + 1}
                                onChange={(_, newPage) => handleChangePage(null, newPage - 1)}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Stack>
                    </Box>
                </Box>
            )}
        </Container>
    );
}; 